import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { MutationConfig } from "@/lib/react-query";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";
import { axiosApi } from "@/Utils";

import { updateCommentV2 } from "../v2/updateComment";

type UpdateCommentRequest = {
  scenarioId: string;
  commentId: string;
  commentData: string;
};

export const updateComment = ({
  scenarioId,
  commentId,
  commentData,
}: UpdateCommentRequest): Promise<any> => {
  return axiosApi.put(
    `/api/v2/scenarios-new/${scenarioId}/comment/${commentId}`,
    { comment: commentData },
    {
      baseURL: INSIGHTS_BACKEND_URL,
    }
  );
};

type UseUpdateCommentOptions = {
  config?: MutationConfig<typeof updateComment>;
  isScenariosApiEnabled?: boolean;
};

export const useUpdateComment = ({
  config,
  isScenariosApiEnabled = false,
}: UseUpdateCommentOptions = {}) => {
  const queryClient = useQueryClient();
  const scenarioBuilderClient = useScenarioBuilderClient();

  const updateCommentFn = (params: UpdateCommentRequest) => {
    if (isScenariosApiEnabled) {
      return updateCommentV2({
        scenarioBuilderClient,
        ...params,
      });
    }
    return updateComment(params);
  };

  return useMutation({
    onSuccess: async (response, variables) => {
      if (isScenariosApiEnabled) {
        await queryClient.invalidateQueries({
          queryKey: ["activity-log-v2", variables.scenarioId],
        });
      } else {
        await queryClient.invalidateQueries({ queryKey: ["activity-logs", variables.scenarioId] });
      }
      toast.success(`Comment successfully updated`);
    },
    onError: (error: any) => {
      console.error("useUpdateComment onError", error);
      toast.error(`Comment failed to update. Please try again.`);
    },
    ...config,
    mutationFn: updateCommentFn,
  });
};
